import { keepPreviousData } from '@tanstack/react-query';
import { useMemo } from 'react';
import { trpc } from '../lib/api';
import type { WorkScheduleStatus } from '../../../shared/types/work-schedule.types';

export interface CalendarScheduleFilter {
  startDate?: Date;
  endDate?: Date;
  statuses?: WorkScheduleStatus[];
  equipmentId?: string;
  workTypeCategoryId?: string | null;
  recurringSourceOnly?: boolean;
  limit?: number;
  offset?: number;
}

export function useCalendarSchedules(
  filters: CalendarScheduleFilter,
  options?: Parameters<typeof trpc.schedule.getCalendarView.useQuery>[1]
) {
  const mergedOptions =
    options && typeof options === 'object' ? (options as Record<string, unknown>) : {};
  return trpc.schedule.getCalendarView.useQuery(filters, {
    placeholderData: keepPreviousData,
    ...mergedOptions
  });
}

export function useRecurringSources(options?: Parameters<typeof trpc.schedule.getRecurringSchedules.useQuery>[1]) {
  return trpc.schedule.getRecurringSchedules.useQuery(undefined, options);
}

export function useMutateWorkSchedule() {
  const utils = trpc.useContext();

  const create = trpc.schedule.create.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.schedule.getCalendarView.invalidate(),
        utils.schedule.getRecurringSchedules.invalidate(),
        utils.schedule.list.invalidate()
      ]);
    }
  });

  const update = trpc.schedule.update.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.schedule.getCalendarView.invalidate(),
        utils.schedule.getRecurringSchedules.invalidate(),
        utils.schedule.list.invalidate()
      ]);
    }
  });

  const remove = trpc.schedule.delete.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.schedule.getCalendarView.invalidate(),
        utils.schedule.getRecurringSchedules.invalidate(),
        utils.schedule.list.invalidate()
      ]);
    }
  });

  return useMemo(
    () => ({
      createSchedule: {
        ...create,
        isLoading: create.isPending,
      },
      updateSchedule: {
        ...update,
        isLoading: update.isPending,
      },
      deleteSchedule: {
        ...remove,
        isLoading: remove.isPending,
      }
    }),
    [create, update, remove]
  );
}
